import dayjs from "dayjs";
import {
  RouteSalesTargetBranch,
  RouteSalesTargetCell,
  RouteSalesTargetDay,
  RouteSalesTargetRoute,
} from "../../types/route-sales-target.type";

/** Typed-over cells by `routeUuid|date`; null = cleared. Only values that differ from the server's. */
export type TargetDraft = Record<string, number | null>;

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;
const SUNDAY = 0;
const DAYS_PER_WEEK = 7;
const PASTE_HEADER_FIRST_DAY = "1";
const COLUMN_SEPARATOR = "\t";
const EXCEL_LABEL_COLUMN = "สาย";

export const cellKeyOf = (routeUuid: string, date: string) => `${routeUuid}|${date}`;

export type ParsedAmount = { ok: true; value: number | null } | { ok: false; message: string };

/** Blank clears the target; otherwise a number ≥ 0 with at most 2 decimals. */
export const parseAmount = (text: string): ParsedAmount => {
  const trimmed = text.replaceAll(",", "").trim();
  if (trimmed === "") return { ok: true, value: null };
  if (trimmed.startsWith("-")) return { ok: false, message: "ตัวเลข ≥ 0" };
  if (!AMOUNT_PATTERN.test(trimmed)) {
    return { ok: false, message: Number.isNaN(Number(trimmed)) ? "กรุณาใส่ตัวเลข" : "ทศนิยมไม่เกิน 2 ตำแหน่ง" };
  }
  return { ok: true, value: Number(trimmed) };
};

export const isPastDay = (date: string, today: string) => date < today;

export const amountOf = (route: RouteSalesTargetRoute, day: RouteSalesTargetDay, draft: TargetDraft) => {
  const key = cellKeyOf(route.route_uuid, day.date);
  return key in draft ? draft[key] : day.amount;
};

/** Sets one cell in the draft, dropping the entry when it is back to the server's value. */
export const setCell = (
  draft: TargetDraft,
  route: RouteSalesTargetRoute,
  day: RouteSalesTargetDay,
  value: number | null,
): TargetDraft => {
  const key = cellKeyOf(route.route_uuid, day.date);
  const { [key]: _removed, ...rest } = draft;
  return value === day.amount ? rest : { ...rest, [key]: value };
};

export const routeTotalOf = (route: RouteSalesTargetRoute, draft: TargetDraft) =>
  route.days.reduce((sum, day) => sum + (amountOf(route, day, draft) ?? 0), 0);

/** A route misses a day when it is planned, not past, and has no target (live, with pending cells). */
export const missingDayCountOf = (route: RouteSalesTargetRoute, draft: TargetDraft, today: string) =>
  route.days.filter((day) => day.is_planned && !isPastDay(day.date, today) && amountOf(route, day, draft) === null).length;

export const missingRouteCountOf = (rows: RouteSalesTargetBranch[], draft: TargetDraft, today: string) =>
  rows.reduce(
    (sum, branch) => sum + branch.routes.filter((route) => missingDayCountOf(route, draft, today) > 0).length,
    0,
  );

export const draftToCells = (draft: TargetDraft): RouteSalesTargetCell[] =>
  Object.entries(draft).map(([key, amount]) => {
    const [routeUuid, date] = key.split("|");
    return { route_uuid: routeUuid, date, amount };
  });

const editableDaysOf = (route: RouteSalesTargetRoute, today: string) =>
  route.days.filter((day) => !isPastDay(day.date, today));

/** One amount on every editable day of the chosen routes, optionally skipping Sundays / no-plan days. */
export const fillMonth = (
  draft: TargetDraft,
  routes: RouteSalesTargetRoute[],
  amount: number,
  options: { skipSundays: boolean; skipUnplanned: boolean },
  today: string,
): TargetDraft =>
  routes.reduce(
    (acc, route) =>
      editableDaysOf(route, today)
        .filter((day) => !(options.skipSundays && dayjs(day.date).day() === SUNDAY))
        .filter((day) => !(options.skipUnplanned && !day.is_planned))
        .reduce((inner, day) => setCell(inner, route, day, amount), acc),
    draft,
  );

/** The previous month's date on the same weekday and the same occurrence (3rd Monday → 3rd Monday), or null past its end. */
export const sameWeekdayLastMonth = (date: string): string | null => {
  const current = dayjs(date);
  const previousStart = current.subtract(1, "month").startOf("month");
  const occurrence = Math.floor((current.date() - 1) / DAYS_PER_WEEK);
  const firstDay = 1 + ((current.day() - previousStart.day() + DAYS_PER_WEEK) % DAYS_PER_WEEK);
  const day = firstDay + occurrence * DAYS_PER_WEEK;
  return day > previousStart.daysInMonth() ? null : previousStart.date(day).format("YYYY-MM-DD");
};

/** Copies last month's targets by weekday onto the editable days; days with nothing to copy stay as they are. */
export const copyFromLastMonth = (
  draft: TargetDraft,
  routes: RouteSalesTargetRoute[],
  previousRoutes: RouteSalesTargetRoute[],
  today: string,
): TargetDraft => {
  const previousByUuid = new Map(previousRoutes.map((route) => [route.route_uuid, route]));
  return routes.reduce((acc, route) => {
    const previousAmountByDate = new Map(
      (previousByUuid.get(route.route_uuid)?.days ?? []).map((day) => [day.date, day.amount]),
    );
    return editableDaysOf(route, today).reduce((inner, day) => {
      const source = sameWeekdayLastMonth(day.date);
      const amount = source ? previousAmountByDate.get(source) ?? null : null;
      return amount === null ? inner : setCell(inner, route, day, amount);
    }, acc);
  }, draft);
};

export interface PastePreview {
  draft: TargetDraft;
  changedCount: number;
  unknownCodes: string[];
  pastCellCount: number;
  invalidCellCount: number;
}

/** Excel layout: first column = route code, then day 1, 2, 3 … ; a header row is skipped. */
export const parsePaste = (
  text: string,
  draft: TargetDraft,
  routes: RouteSalesTargetRoute[],
  today: string,
): PastePreview => {
  const routeByCode = new Map(routes.map((route) => [route.route_code, route]));
  const unknownCodes: string[] = [];
  let next = draft;
  let pastCellCount = 0;
  let invalidCellCount = 0;
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  lines.forEach((line, lineIndex) => {
    const [rawCode, ...cells] = line.split(COLUMN_SEPARATOR);
    const code = rawCode.trim();
    const route = routeByCode.get(code);
    const isHeader =
      lineIndex === 0 && !route && (code === EXCEL_LABEL_COLUMN || cells[0]?.trim() === PASTE_HEADER_FIRST_DAY);
    if (isHeader) return;
    if (!route) {
      if (!unknownCodes.includes(code)) unknownCodes.push(code);
      return;
    }
    cells.forEach((cell, index) => {
      const day = route.days[index];
      if (!day || cell.trim() === "") return;
      if (isPastDay(day.date, today)) {
        pastCellCount += 1;
        return;
      }
      const parsed = parseAmount(cell);
      if (!parsed.ok || parsed.value === null) {
        invalidCellCount += 1;
        return;
      }
      next = setCell(next, route, day, parsed.value);
    });
  });
  const changedCount = Object.keys(next).filter((key) => next[key] !== draft[key]).length;
  return { draft: next, changedCount, unknownCodes, pastCellCount, invalidCellCount };
};

/** The on-screen table in the paste layout, so it can be edited in Excel and pasted back. */
export const tableAsExcel = (routes: RouteSalesTargetRoute[], draft: TargetDraft): string => {
  const dayCount = routes[0]?.days.length ?? 0;
  const header = [EXCEL_LABEL_COLUMN, ...Array.from({ length: dayCount }, (_, index) => String(index + 1))];
  const body = routes.map((route) => [
    route.route_code,
    ...route.days.map((day) => String(amountOf(route, day, draft) ?? "")),
  ]);
  return [header, ...body].map((row) => row.join(COLUMN_SEPARATOR)).join("\n");
};
